/**
 * Builds side-navigation modal payloads (per J-code document lists) from a
 * legacy {@link MedicalPaOrder} for the doc viewer with title + prev/next.
 *
 * @module createDataForDocWithSideNavModal
 */
import { getViewerDocumentTitleByDocType } from "../constants/statusToDocMap/statusToDocMap";
import { AuthOnFile, MedicalPaOrder } from "../data-model/medicalPaOrdersModel";

/**
 * Flattens all J-codes from auth entries and maps each to nav items for its documents.
 *
 * @param orderData - Medical PA order including `auth_on_file.entries`
 * @returns Array of `{ description, id, documents }` for the modal, or `undefined` when no J-codes
 */
export const generateDataForDocWithSideNavModalFromSingleMedicalPaOrder = (
  orderData: MedicalPaOrder,
) => {
  return generateDataForDocWithSideNavModalFromAuthOnFile(
    orderData?.auth_on_file,
  );
};

/**
 * Builds side-navigation modal items from an `auth_on_file` object directly.
 * Flattens J-codes from auth entries and maps each to nav items with documents.
 *
 * @param authOnFile - The auth-on-file data containing entries with J-codes
 * @returns Array of `{ description, id, documents }` for the modal, or `undefined` when no J-codes
 */
export const generateDataForDocWithSideNavModalFromAuthOnFile = (
  authOnFile?: AuthOnFile,
) => {
  const entries = authOnFile?.entries ?? authOnFile?.auth_entries;
  const allJCodesData = entries
    ?.map((authEntry) => authEntry.j_codes)
    .filter((jCode) => jCode && jCode?.length > 0)
    .flat();

  return allJCodesData?.map((jCode) => {
    const docs = jCode?.documents?.map((doc, index) => {
      return {
        title: getViewerDocumentTitleByDocType(doc?.doc_type ?? ""),
        type: doc?.doc_type,
        path: doc?.doc_path,
        id: jCode?.id ?? "",
        showPrevious: index > 0,
        showNext: index < (jCode?.documents?.length ?? 0) - 1,
      };
    });
    return {
      description:
        (jCode?.j_code ?? "") +
        " - " +
        (jCode?.medicine_name ?? jCode?.description ?? ""),
      id: jCode?.id,
      documents: docs,
    };
  });
};
